import React, { useState, useEffect, useCallback } from 'react';
import { openInAppFilePreview } from '../../utils/filePreviewBus';
import {
  Box, Typography, Button, Stack, CircularProgress, Alert, Snackbar,
  Fade, useMediaQuery, Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, Autocomplete, Chip, IconButton
} from '@mui/material';
import {
  Refresh, Add, Edit, Delete, Visibility, NavigateBefore, NavigateNext,
  Store
} from '@mui/icons-material';
import Swal from 'sweetalert2';
import { useSelector } from 'react-redux';
import {
  getSupplierBarang, getSupplierBarangById,
  createSupplierBarang, updateSupplierBarang, deleteSupplierBarang
} from '../API/supplierPenyedia';
import { getBantuan } from '../API/bantuan';
import { ROLES } from '../../config/roleConfig';

const formatRp = (n) => (!n && n !== 0) ? '-' : 'Rp ' + Number(n).toLocaleString('id-ID');
const imgUrl = (filename) => filename ? `${process.env.REACT_APP_URL}/files/images/${filename}` : null;

const getTotalTransaksi = (row) =>
  row?.totalTransaksiKeseluruhan ?? (row?.Pembelian || []).reduce(
    (sum, p) => sum + (Number(p.totalTransaksi) || 0), 0
  );

const getLinkedBantuanIds = (row) => {
  if (row?.linkedBantuanIds?.length) return row.linkedBantuanIds;
  if (row?.BantuanId) return [row.BantuanId];
  const fromPembelian = [...new Set((row?.Pembelian || []).map((p) => p.BantuanId).filter(Boolean))];
  return fromPembelian;
};

const renderBantuanChips = (row) => {
  const ids = getLinkedBantuanIds(row);
  if (!ids.length) return '-';
  return (
    <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
      {ids.map((id) => (
        <Chip key={id} size="small" label={`#${id}`} color="primary" variant="outlined" />
      ))}
    </Stack>
  );
};

const DataSupplier = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [totalItems, setTotalItems] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [search, setSearch] = useState('');
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const [formOpen, setFormOpen] = useState(false);
  const [formMode, setFormMode] = useState('add');
  const [saving, setSaving] = useState(false);
  const [detailOpen, setDetailOpen] = useState(false);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  // Bantuan dropdown options
  const [bantuanOpts, setBantuanOpts] = useState([]);

  // ============================================================
  // ROLE-BASED ACCESS CONTROL
  // ============================================================
  const user = useSelector((state) => state.authAdmin?.user);
  const userRole = user?.role || '';
  const userId = user?.id || null;

  const isAdmin = userRole === ROLES.ADMIN;
  const isTFL = userRole === ROLES.TFL;
  const isSupplier = userRole === ROLES.SUPPLIER;

  // Hak akses berdasarkan role
  const canCreate = isAdmin || isTFL;      // Admin & TFL bisa tambah
  const canEdit = isAdmin || isTFL || isSupplier;  // Admin, TFL, Supplier bisa edit
  const canDelete = isAdmin;               // Hanya Admin yang bisa hapus
  const canViewAll = isAdmin;              // Admin bisa lihat semua
  const canViewOwn = isTFL || isSupplier;  // TFL & Supplier hanya lihat milik sendiri
  const canCreateAccount = isAdmin || isTFL; // Admin & TFL bisa buat akun login supplier

  // Form state
  const emptyForm = {
    id: null, BantuanId: '', namaSupplier: '', alamatSupplier: '',
    nomorWA: '', npwpOrDllSupplier: '', informasiLanjut: '',
    nomorRekening: '', namaPemilik: '',
    createAccount: true,
    username: '', email: '', password: '',
  };
  const [form, setForm] = useState(emptyForm);
  const [selBantuan, setSelBantuan] = useState(null);

  const isMobile = useMediaQuery('(max-width:800px)');
  const notify = (message, severity = 'success') =>
    setSnackbar({ open: true, message, severity });

  // ============================================================
  // FETCH DATA - FILTER BERDASARKAN ROLE
  // ============================================================
  const fetchList = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getSupplierBarang({ page, limit, search });
      
      let filteredData = res.data.data || [];
      
      // TFL: hanya lihat supplier yang terkait dengan bantuan dampingannya
      if (isTFL) {
        // Filter berdasarkan BantuanId yang terkait dengan TFL
        // Ini harus disesuaikan dengan backend yang sudah memfilter berdasarkan TFL
        filteredData = filteredData.filter(item => {
          // Cek apakah supplier terkait dengan bantuan TFL
          const bantuanIds = getLinkedBantuanIds(item);
          // Di sini perlu pengecekan dari backend, karena TFL hanya bisa melihat supplier yang terhubung dengan bantuannya
          return true; // Sementara, nanti di backend difilter
        });
      }
      
      // Supplier: hanya lihat data miliknya sendiri
      if (isSupplier) {
        filteredData = filteredData.filter(item => item.adminId === userId);
      }
      
      setRows(filteredData);
      setTotalItems(res.data.totalItems ?? 0);
      setTotalPages(res.data.totalPages ?? 1);
    } catch (err) {
      console.error(err);
      notify('Gagal mengambil data supplier', 'error');
    } finally {
      setLoading(false);
    }
  }, [page, limit, search, isTFL, isSupplier, userId]);

  const fetchBantuanOpts = useCallback(async () => {
    try {
      const res = await getBantuan({ page: 1, limit: 100 });
      const list = (res.data.data || []).map((b) => ({
        id: b.id,
        label: `#${b.id} - ${b.pemohon?.nama || '-'} (${b.JenisBantuan || '-'})`,
      }));
      setBantuanOpts(list);
    } catch (err) { console.error(err); }
  }, []);

  useEffect(() => {
    const t = setTimeout(() => fetchList(), 400);
    return () => clearTimeout(t);
  }, [fetchList]);

  useEffect(() => { fetchBantuanOpts(); }, [fetchBantuanOpts]);

  // ============================================================
  // CRUD HANDLERS
  // ============================================================
  const openAdd = () => {
    // Cek akses create
    if (!canCreate) {
      notify('Anda tidak memiliki akses untuk menambah supplier', 'warning');
      return;
    }
    setFormMode('add');
    setForm(emptyForm);
    setSelBantuan(null);
    setFormOpen(true);
  };

  const openEdit = async (row) => {
    // Cek akses edit
    if (!canEdit) {
      notify('Anda tidak memiliki akses untuk mengedit supplier', 'warning');
      return;
    }
    
    // Supplier hanya bisa edit data miliknya sendiri
    if (isSupplier && row.adminId !== userId) {
      notify('Anda hanya bisa mengedit data supplier Anda sendiri', 'warning');
      return;
    }
    
    setFormMode('edit');
    try {
      const res = await getSupplierBarangById(row.id);
      const d = res.data.data;
      setForm({
        id: d.id,
        BantuanId: d.BantuanId,
        namaSupplier: d.namaSupplier || '',
        alamatSupplier: d.alamatSupplier || '',
        nomorWA: d.nomorWA || '',
        nomorRekening: d.nomorRekening || '',
        namaPemilik: d.namaPemilik || '',
        npwpOrDllSupplier: d.npwpOrDllSupplier || '',
        informasiLanjut: d.informasiLanjut || '',
      });
      setSelBantuan(bantuanOpts.find((b) => b.id === d.BantuanId) || (d.BantuanId ? { id: d.BantuanId, label: `#${d.BantuanId}` } : null));
    } catch (err) {
      notify('Gagal memuat detail', 'error');
    }
    setFormOpen(true);
  };

  const handleSave = async () => {
    if (!form.namaSupplier.trim()) return notify('Nama Toko Supplier wajib diisi', 'warning');
    if (!form.alamatSupplier.trim()) return notify('Alamat supplier wajib diisi', 'warning');

    setSaving(true);
    try {
      if (formMode === 'add') {
        const payload = {
          namaSupplier: form.namaSupplier.trim(),
          alamatSupplier: form.alamatSupplier.trim(),
          nomorWA: form.nomorWA.trim(),
          nomorRekening: form.nomorRekening.trim(),
          namaPemilik: form.namaPemilik.trim(),
          npwpOrDllSupplier: form.npwpOrDllSupplier.trim(),
          informasiLanjut: form.informasiLanjut || '',
          BantuanId: selBantuan?.id || '',
        };
        // Akun supplier - admin & TFL yang bisa membuat akun
        if (canCreateAccount && form.createAccount) {
          if (!form.username.trim() || !form.email.trim() || !form.password) {
            setSaving(false);
            return notify('Username, email, dan password akun supplier wajib diisi', 'warning');
          }
          payload.username = form.username.trim();
          payload.email = form.email.trim();
          payload.password = form.password;
        }
        await createSupplierBarang(payload);
        notify('Supplier berhasil ditambahkan');
      } else {
        await updateSupplierBarang(form.id, {
          namaSupplier: form.namaSupplier.trim(),
          alamatSupplier: form.alamatSupplier.trim(),
          nomorWA: form.nomorWA.trim(),
          nomorRekening: form.nomorRekening.trim(),
          namaPemilik: form.namaPemilik.trim(),
          npwpOrDllSupplier: form.npwpOrDllSupplier.trim(),
          informasiLanjut: form.informasiLanjut || '',
        });
        notify('Supplier berhasil diupdate');
      }
      setFormOpen(false);
      fetchList();
    } catch (err) {
      console.error(err);
      notify(err.response?.data?.message || 'Gagal menyimpan', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = (row) => {
    // Cek akses delete - hanya admin
    if (!canDelete) {
      notify('Hanya admin yang dapat menghapus supplier', 'warning');
      return;
    }
    
    Swal.fire({
      title: 'Hapus supplier ini?', text: row.namaSupplier,
      icon: 'warning', showCancelButton: true,
      confirmButtonColor: '#d33', confirmButtonText: 'Hapus', cancelButtonText: 'Batal'
    }).then(async (r) => {
      if (!r.isConfirmed) return;
      try {
        await deleteSupplierBarang(row.id);
        notify('Supplier dihapus');
        fetchList();
      } catch (err) { notify('Gagal menghapus', 'error'); }
    });
  };

  const openDetail = async (row) => {
    // Supplier hanya bisa lihat detail miliknya sendiri
    if (isSupplier && row.adminId !== userId) {
      notify('Anda hanya bisa melihat detail supplier Anda sendiri', 'warning');
      return;
    }
    
    setDetailOpen(true); setDetailLoading(true); setDetail(null);
    try {
      const res = await getSupplierBarangById(row.id);
      setDetail(res.data.data);
    } catch (err) { notify('Gagal memuat detail', 'error'); }
    finally { setDetailLoading(false); }
  };

  const totalTransaksi = getTotalTransaksi(detail);
  const pembelianList = detail?.Pembelian || [];

  const contentStyle = isMobile
    ? {}
    : { marginLeft: "250px", marginRight: "20px" };

  return (
    <div className="table-responsive rounded border">
      <div className="main-content" style={contentStyle}>
        <Box sx={{ mt: 1, mb: 2, width: '100%', maxWidth: '100%' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2, justifyContent: 'space-between', flexWrap: 'wrap' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Store color="primary" />
              <Typography variant="h5" fontWeight={700}>Data Supplier Penyedia</Typography>
              <Chip 
                size="small" 
                label={userRole || 'Guest'} 
                color={isAdmin ? 'error' : isTFL ? 'warning' : isSupplier ? 'info' : 'default'}
                variant="outlined"
              />
            </Box>
            <Stack direction="row" spacing={1}>
              {/* Tombol Tambah - hanya Admin & TFL */}
              {canCreate && (
                <Button variant="contained" color="success" startIcon={<Add />} size="small" onClick={openAdd}>
                  Tambah
                </Button>
              )}
              <Button variant="outlined" startIcon={<Refresh />} size="small" onClick={fetchList}>
                Muat Ulang
              </Button>
            </Stack>
          </Box>

          <Box sx={{ mb: 2 }}>
            <TextField size="small" placeholder="Cari Nama Toko Supplier..." value={search}
              onChange={(e) => { setSearch(e.target.value); setPage(1); }}
              sx={{ minWidth: 260 }} />
          </Box>

          <Box sx={{ overflowX: 'auto', bgcolor: '#fff', border: '1px solid #e0e0e0', borderRadius: 1 }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
              <thead>
                <tr>
                  {['No.', 'Nama Toko Supplier', 'Akun Login', 'Kontak', 'Bantuan Terlayani', 'Aksi'].map((h) => (
                    <th key={h} style={{ 
                      textAlign: h === 'Aksi' ? 'center' : 'left', 
                      padding: '10px', 
                      borderBottom: '1px solid #e0e0e0', 
                      whiteSpace: 'nowrap' 
                    }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan={6} style={{ padding: '32px', textAlign: 'center' }}><CircularProgress size={26} /></td></tr>
                ) : rows.length === 0 ? (
                  <tr><td colSpan={6} style={{ padding: '32px', textAlign: 'center', color: '#888' }}>
                    {isSupplier
                      ? 'Belum ada data supplier untuk akun Anda. Klik Tambah untuk melengkapi profil toko.'
                      : 'Tidak ada data.'}
                  </td></tr>
                ) : rows.map((row, i) => (
                  <tr key={row.id} style={{ borderBottom: '1px solid #f0f0f0' }}>
                    <td style={{ padding: '10px' }}>{(page - 1) * limit + i + 1}</td>
                    <td style={{ padding: '10px', fontWeight: 600 }}>
                      {row.namaSupplier}
                      <Box component="span" sx={{ display: 'block', fontSize: 12, color: 'text.secondary' }}>{row.alamatSupplier}</Box>
                    </td>
                    <td style={{ padding: '10px' }}>
                      {row.Admin ? (
                        <>
                          <div>{row.Admin.username}</div>
                          <div style={{ fontSize: 12, color: 'text.secondary' }}>{row.Admin.email}</div>
                        </>
                      ) : (
                        <span style={{ color: '#888' }}>-</span>
                      )}
                    </td>
                    <td style={{ padding: '10px' }}>
                      <div>WA: {row.nomorWA}</div>
                      <div style={{ fontSize: 12, color: 'text.secondary' }}>{row.npwpOrDllSupplier}</div>
                    </td>
                     <td style={{ padding: '10px' }}>
                      <div>Rekening: {row.nomorRekening}</div>
                      <div style={{ fontSize: 12, color: 'text.secondary' }}>{row.npwpOrDllSupplier}</div>
                    </td>
                    <td style={{ padding: '10px' }}>
                      {renderBantuanChips(row)}
                    </td>
                    <td style={{ padding: '10px', whiteSpace: 'nowrap', width: '1%', textAlign: 'center', verticalAlign: 'middle' }}>
                      <Box sx={{ display: 'flex', flexDirection: 'row', justifyContent: 'center', alignItems: 'center', gap: '2px' }}>
                        {/* Tombol Detail - semua role bisa */}
                        <IconButton size="small" disableRipple disableFocusRipple
                          onClick={() => openDetail(row)} title="Detail"
                          sx={{ p: '2px', color: '#1976d2', '&:hover': { background: 'transparent', color: '#1565c0' } }}>
                          <Visibility fontSize="small" />
                        </IconButton>
                        
                        {/* Tombol Edit - Admin, TFL, dan Supplier (milik sendiri) */}
                        {(canEdit && (isAdmin || isTFL || (isSupplier && row.adminId === userId))) && (
                          <IconButton size="small" disableRipple disableFocusRipple
                            onClick={() => openEdit(row)} title="Edit"
                            sx={{ p: '2px', color: '#ed6c02', '&:hover': { background: 'transparent', color: '#e65100' } }}>
                            <Edit fontSize="small" />
                          </IconButton>
                        )}
                        
                        {/* Tombol Hapus - hanya Admin */}
                        {canDelete && (
                          <IconButton size="small" disableRipple disableFocusRipple
                            onClick={() => handleDelete(row)} title="Hapus"
                            sx={{ p: '2px', color: '#d32f2f', '&:hover': { background: 'transparent', color: '#b71c1c' } }}>
                            <Delete fontSize="small" />
                          </IconButton>
                        )}
                      </Box>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Box>

          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 2, flexWrap: 'wrap', gap: 1 }}>
            <Stack direction="row" spacing={1} alignItems="center">
              <Box component="select" value={limit}
                onChange={(e) => { setLimit(Number(e.target.value)); setPage(1); }}
                sx={{ px: 1, py: 0.8, border: '1px solid #ccc', borderRadius: 1, fontSize: 14 }}>
                {[10, 25, 50].map((n) => <option key={n} value={n}>{n}</option>)}
              </Box>
              <Typography variant="body2" color="text.secondary">Hal. {page} / {totalPages} ({totalItems} data)</Typography>
            </Stack>
            <Stack direction="row" spacing={1}>
              <Button variant="outlined" size="small" startIcon={<NavigateBefore />} disabled={page <= 1}
                onClick={() => setPage((p) => Math.max(1, p - 1))}>{!isMobile && 'Prev'}</Button>
              <Button variant="outlined" size="small" endIcon={<NavigateNext />} disabled={page >= totalPages}
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}>{!isMobile && 'Next'}</Button>
            </Stack>
          </Box>

          {/* MODAL FORM - sama seperti sebelumnya */}
          <Dialog open={formOpen} onClose={() => setFormOpen(false)} maxWidth="md" fullWidth>
            <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Store color="primary" /> {formMode === 'add' ? 'Tambah Supplier Barang' : 'Edit Supplier Barang'}
            </DialogTitle>
            <DialogContent dividers>
              <Stack spacing={2} sx={{ mt: 1 }}>
                <Autocomplete
                  options={bantuanOpts}
                  value={selBantuan}
                  getOptionLabel={(o) => o?.label ?? ''}
                  isOptionEqualToValue={(o, v) => o?.id === v?.id}
                  onChange={(e, val) => { setSelBantuan(val); setForm((p) => ({ ...p, BantuanId: val?.id || '' })); }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Bantuan awal (opsional)"
                      size="small"
                      helperText="Satu supplier dapat melayani banyak bantuan lewat transaksi pembelian"
                    />
                  )}
                />
                <TextField label="Nama Toko Supplier" size="small" required value={form.namaSupplier}
                  onChange={(e) => setForm((p) => ({ ...p, namaSupplier: e.target.value }))} />
                <TextField label="Alamat Supplier" size="small" required multiline rows={2} value={form.alamatSupplier}
                  onChange={(e) => setForm((p) => ({ ...p, alamatSupplier: e.target.value }))} />
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                  <TextField label="Nomor WA" size="small"  type='number' fullWidth value={form.nomorWA}
                    onChange={(e) => setForm((p) => ({ ...p, nomorWA: e.target.value }))} />
                    
                  <TextField label="NPWP" type="number" size="small" fullWidth value={form.npwpOrDllSupplier}
                    onChange={(e) => setForm((p) => ({ ...p, npwpOrDllSupplier: e.target.value }))} />
                     <TextField label="No Rekening" type="number"size="small" fullWidth value={form.nomorRekening}
                    onChange={(e) => setForm((p) => ({ ...p, nomorRekening: e.target.value }))} />
                    <TextField label="Nama Pemilik" size="small" fullWidth value={form.namaPemilik}
                    onChange={(e) => setForm((p) => ({ ...p, namaPemilik: e.target.value }))} />
                </Stack>
                <TextField label="Informasi Lanjut (opsional)" size="small" multiline rows={2} value={form.informasiLanjut}
                  onChange={(e) => setForm((p) => ({ ...p, informasiLanjut: e.target.value }))} />

                {/* Akun supplier - admin & TFL saat tambah */}
                {formMode === 'add' && canCreateAccount && (
                  <Box sx={{ border: '1px dashed #bdbdbd', borderRadius: 1, p: 2, bgcolor: '#fafafa' }}>
                    <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                      <Chip size="small" color="info" label="Akun Login Supplier" />
                      <Box component="label" sx={{ display: 'flex', alignItems: 'center', fontSize: 13 }}>
                        <input type="checkbox" checked={form.createAccount}
                          onChange={(e) => setForm((p) => ({ ...p, createAccount: e.target.checked }))} />
                        <span style={{ ml: 1 }}>Buat akun login untuk supplier ini</span>
                      </Box>
                    </Stack>
                    {form.createAccount && (
                      <Stack spacing={1.5}>
                        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                          <TextField label="Username" size="small" fullWidth required value={form.username}
                            onChange={(e) => setForm((p) => ({ ...p, username: e.target.value }))} />
                          <TextField label="Email" size="small" type="email" fullWidth required value={form.email}
                            onChange={(e) => setForm((p) => ({ ...p, email: e.target.value }))} />
                        </Stack>
                        <TextField label="Password" size="small" type="password" fullWidth required value={form.password}
                          onChange={(e) => setForm((p) => ({ ...p, password: e.target.value }))} />
                        <Typography variant="caption" color="text.secondary">
                          Supplier akan login dengan akun ini (role: Supplier Penyedia) untuk input pembelian sendiri.
                        </Typography>
                      </Stack>
                    )}
                  </Box>
                )}
              </Stack>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setFormOpen(false)}>Batal</Button>
              <Button variant="contained" disabled={saving} onClick={handleSave}>{saving ? 'Menyimpan...' : 'Simpan'}</Button>
            </DialogActions>
          </Dialog>

          {/* MODAL DETAIL - sama seperti sebelumnya */}
          <Dialog open={detailOpen} onClose={() => setDetailOpen(false)} maxWidth="md" fullWidth>
            <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Store color="primary" /> Detail Supplier
            </DialogTitle>
            <DialogContent dividers>
              {detailLoading ? (
                <Box sx={{ textAlign: 'center', py: 4 }}><CircularProgress /></Box>
              ) : detail ? (
                <Stack spacing={2}>
                  {/* ... konten detail sama seperti sebelumnya ... */}
                  <Box>
                    <Typography variant="overline" color="text.secondary">Supplier</Typography>
                    <Typography variant="h6">{detail.namaSupplier}</Typography>
                    <Typography variant="body2">{detail.alamatSupplier}</Typography>
                    <Stack direction="row" spacing={1} sx={{ mt: 1, flexWrap: 'wrap', gap: 1 }}>
                      <Chip size="small" label={`WA: ${detail.nomorWA}`} />
                      <Chip size="small" label={`Rekening: ${detail.nomorRekening || '-'}`} />
                      <Chip size="small" label={`Pemilik: ${detail.namaPemilik || '-'}`} />
                      <Chip size="small" label={detail.npwpOrDllSupplier} />
                      {renderBantuanChips(detail)}
                    </Stack>
                    {detail.informasiLanjut && <Typography variant="body2" sx={{ mt: 1 }}>{detail.informasiLanjut}</Typography>}
                    {detail.Admin && (
                      <Box sx={{ mt: 1 }}>
                        <Typography variant="overline" color="text.secondary">Akun Login</Typography>
                        <Typography variant="body2">Username: {detail.Admin.username}</Typography>
                        <Typography variant="body2">Email: {detail.Admin.email}</Typography>
                      </Box>
                    )}
                  </Box>
                  <Box>
                    <Typography variant="overline" color="text.secondary">Foto Transaksi Terbaru</Typography>
                    <Stack direction="row" spacing={2} sx={{ mt: 1 }}>
                      {pembelianList[0]?.fotoKwitansi && (
                        <Button size="small" onClick={() => openInAppFilePreview({ url: imgUrl(pembelianList[0].fotoKwitansi), title: "Kwitansi" })}>
                          <img src={imgUrl(pembelianList[0].fotoKwitansi)} alt="kwitansi" style={{ width: 100, height: 100, objectFit: 'cover', borderRadius: 8, border: '1px solid #e0e0e0' }} />
                        </Button>
                      )}
                      {pembelianList[0]?.fotoBarang && (
                        <Button size="small" onClick={() => openInAppFilePreview({ url: imgUrl(pembelianList[0].fotoBarang), title: "Foto Barang" })}>
                          <img src={imgUrl(pembelianList[0].fotoBarang)} alt="barang" style={{ width: 100, height: 100, objectFit: 'cover', borderRadius: 8, border: '1px solid #e0e0e0' }} />
                        </Button>
                      )}
                    </Stack>
                  </Box>
                  <Box>
                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                      <Typography variant="overline" color="text.secondary">
                        Riwayat Transaksi ({pembelianList.length})
                      </Typography>
                      <Typography variant="h6" color="primary" fontWeight={700}>{formatRp(totalTransaksi)}</Typography>
                    </Stack>
                    {pembelianList.map((pembelian) => (
                      <Box key={pembelian.id} sx={{ mt: 2 }}>
                        <Typography variant="subtitle2" sx={{ mb: 1 }}>
                          #{pembelian.id} · {new Date(pembelian.createdAt).toLocaleDateString('id-ID')} · {formatRp(pembelian.totalTransaksi)}
                        </Typography>
                        <Box sx={{ overflowX: 'auto' }}>
                          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                            <thead style={{ backgroundColor: '#f5f7fa' }}>
                              <tr>
                                {['#', 'Nama Barang', 'Harga Satuan', 'Jumlah', 'Subtotal'].map((h) => (
                                  <th key={h} style={{ textAlign: 'left', padding: '8px', borderBottom: '1px solid #e0e0e0' }}>{h}</th>
                                ))}
                              </tr>
                            </thead>
                            <tbody>
                              {(pembelian.DetailTransaksi || []).map((d, i) => (
                                <tr key={d.id} style={{ borderBottom: '1px solid #f0f0f0' }}>
                                  <td style={{ padding: '8px' }}>{i + 1}</td>
                                  <td style={{ padding: '8px' }}>{d.namaBarang}</td>
                                  <td style={{ padding: '8px' }}>{formatRp(d.hargaSatuan)}</td>
                                  <td style={{ padding: '8px' }}>{d.jumlahBarang}</td>
                                  <td style={{ padding: '8px' }}>{formatRp(d.totalTransaksi)}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </Box>
                      </Box>
                    ))}
                  </Box>
                </Stack>
              ) : <Alert severity="warning">Data tidak ditemukan.</Alert>}
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setDetailOpen(false)}>Tutup</Button>
            </DialogActions>
          </Dialog>

          <Snackbar open={snackbar.open} autoHideDuration={4000}
            onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
            TransitionComponent={Fade} anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}>
            <Alert onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
              severity={snackbar.severity} variant="filled" sx={{ width: '100%' }}>
              {snackbar.message}
            </Alert>
          </Snackbar>
        </Box>
      </div>
    </div>
  );
};

export default DataSupplier;